/**
 * Where a person is standing once the sign-in library has been answered.
 *
 * `/auth/ms/callback` is Microsoft's delivery address, not a page of this
 * console. The service answers every unknown path with `index.html`, so the
 * console boots there, reads the response, establishes the session, and then
 * has nothing that takes anybody away again. `App.tsx` renders its own "There
 * is no page at this address" over a sign-in that has just succeeded.
 *
 * Measured on dev on 2026-08-29: authenticated, navigation rendered, sitting
 * on a 404.
 *
 * OLDER THAN THE FRAME REPAIR, and which it is decides how this is written.
 * The catch-all route is from `4ce61cb` on 5 August and the redirect URI from
 * `e1a5c9a`, the first console commit. `b4a09c4` touched three files and none
 * of them was a router or a provider. So the frame repair did not produce
 * this, it made people reach it: before it, the expired-session path died in
 * `BrowserAuthError: timed_out` and the Microsoft button was held back on dev
 * until the manifest was confirmed, so nobody had ever completed a redirect
 * sign-in here to be left standing anywhere.
 *
 * NOT `navigateToLoginRequestUrl`. That option is MSAL's own bounce, taken
 * before the response is read, and it loses the token in a reload, which
 * Service Portal paid to discover and `auth.tsx` records. This is the step
 * after: the response is already consumed and the session already exists.
 * Turning that option back on would reintroduce their defect and would still
 * not fix this one.
 */

/** Also the path in the `redirectUri` MSAL is configured with, taken from
 *  here, so the address Microsoft returns to and the address we leave cannot
 *  drift apart. */
export const MSAL_REDIRECT_PATH = '/auth/ms/callback'

const CONSOLE_HOME = '/'

/**
 * Where to go once the boot has finished, or null to stay where you are.
 *
 * Stated over the address, not over the outcome of the sign-in. A rule
 * conditioned on "a response was processed" still leaves the reload, the
 * refused sign-in and the pasted link standing on the 404, and each of those
 * is a person reading "there is no page at this address" while holding a
 * working session. The address is simply never one of ours.
 *
 * Returning null rather than the home path is the half that has to be tested.
 * A rule that always answers `/` sends every deep link to the Overview, so
 * somebody who followed a link to one decision and was asked to sign in
 * arrives somewhere else, and that failure is invisible to a test which only
 * ever boots at the callback address.
 */
export function landingAfterSignIn(pathname: string): string | null {
  const withoutTrailingSlash = pathname.replace(/\/+$/, '')
  return withoutTrailingSlash === MSAL_REDIRECT_PATH ? CONSOLE_HOME : null
}
